import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { authApi } from './authAPI';
import { jwtDecode } from 'jwt-decode';
import { UserType } from '../users/usersAPI';
import { DateTime } from 'luxon';
import { startAppListening } from '../../app/listenerMiddleware';
import { GetSitePermissionsResponse, sitesApi } from '../sites/sitesAPI';
import { createSelector, type Draft } from '@reduxjs/toolkit';
import { SiteType } from '../sites/sitesAPI';
import { compare } from 'compare-versions';
import debounce from 'lodash/debounce';
import { SiteFilterBy } from '../sites/sitesSlice';

export enum Permissions {
  AdminUser = 1,
  SiteManagerUser = 2,
  EditOtherUsers = 3,
  ReceiveLeoStatusEmail = 4,
  OptLeoStatusEmail = 5,
  ReceiveAnalyticReportEmails = 6,
  OptRackUsageEmail = 7,
  OptLiquidLevelLeakEmail = 8,
  OptCaseTempEmail = 9,
  RemoteAccessLeo = 10,
  RemoteAccessController = 11,
  ViewFloorplan = 12,
  EditFloorplan = 13,
  ViewStore = 14,
  EditPhoneNumbers = 15,
  AddUser = 16,
  RemoteAccessPorts = 17,
  EditControllerBackups = 18,
  OptAlarmReportEmail = 19,
  EditLeoAlertSchedule = 20,
  RefrigerantTracking = 21,
}

export interface DecodedToken {
  user_id: number;
  username: string;
  admin_type: UserType;
  store_list: number[];
  store_permissions: {
    [siteId: number]: number[];
  };
  user_permissions: null; // this will change

  minimal: false;
  exp: number;
  iat: number;
}
export interface MinimalDecodedToken {
  user_id: number;
  admin_type: UserType;
  exp: number;
  iat: number;
  minimal: true;
  store_list: never[];
  store_permissions: never[];
  user_permissions: never[];
  username: string;
}

export interface AuthState {
  isAuthenticated: boolean;
  hasTokenAwaitingTwoFactor: boolean;
  username: string;
  userId: number;
  token: string;
  adminType: UserType;
  siteList: number[];
  filteredSiteList: number[];
  setFilteredSitesList: boolean;
  sitePermissionsList: GetSitePermissionsResponse;
  userSitesPermissions: {
    [siteId: number]: number[];
  };
}

const AddedSitesKey = 'addedSites';

function getInitialState(): AuthState {
  const token = localStorage.getItem('token');
  const minToken = localStorage.getItem('minToken');
  const awaitingTwoFactor = localStorage.getItem('awaitingTwoFactor');
  if (token) {
    const decoded = jwtDecode(token) as DecodedToken;

    // Check the decoded.exp time
    const nowSecs = DateTime.now().toUTC().toUnixInteger();
    const expiresIn = ((decoded.exp - nowSecs) / 60).toFixed(2);
    console.info(
      `getInitialState: token expires in %c ${expiresIn} %cminutes`,
      'font-weight: bold; color: yellow; background-color: black;',
    );

    if (nowSecs >= decoded.exp) {
      console.info(
        '%clogging out because token expired via init check (checking stored token on first load)',
        'color: orange',
      );
      localStorage.removeItem('token');
    } else {
      decoded.store_list = decoded.store_list.map((v) => +v);
      return {
        token,
        isAuthenticated: true,
        hasTokenAwaitingTwoFactor: false,
        username: decoded.username,
        userId: decoded.user_id,
        adminType: decoded.admin_type,
        siteList: decoded.store_list,
        filteredSiteList: [], //decoded.store_list, // need to check early for filtered list
        setFilteredSitesList: false,
        sitePermissionsList: [],
        userSitesPermissions: decoded.store_permissions,
      };
    }
  } else if (minToken && awaitingTwoFactor) {
    const minDecoded = jwtDecode(minToken) as MinimalDecodedToken;
    const nowSecs = DateTime.now().toUTC().toUnixInteger();
    const expiresIn = ((minDecoded.exp - nowSecs) / 60).toFixed(2);
    console.info('minToken Expires In: ', expiresIn);
    if (nowSecs >= minDecoded.exp) {
      // remove all credentials
      localStorage.removeItem('minToken');
      localStorage.removeItem('awaitingTwoFactor');
    } else {
      return {
        token: minToken,
        isAuthenticated: false,
        hasTokenAwaitingTwoFactor: true,
        username: minDecoded.username,
        userId: minDecoded.user_id,
        adminType: minDecoded.admin_type,
        siteList: minDecoded.store_list,
        filteredSiteList: [],
        setFilteredSitesList: false,
        sitePermissionsList: [],
        userSitesPermissions: minDecoded.store_permissions,
      };
    }
  }

  return {
    isAuthenticated: false,
    hasTokenAwaitingTwoFactor: false,
    token: '',
    adminType: 'user',
    username: '',
    userId: 0,
    siteList: [],
    filteredSiteList: [],
    setFilteredSitesList: false,
    sitePermissionsList: [],
    userSitesPermissions: {},
  };
}

function handleCredentials(
  state: Draft<AuthState>,
  token: string,
  update = false,
) {
  const decoded = jwtDecode(token) as DecodedToken;
  const nowSecs = DateTime.now().toUTC().toUnixInteger();
  const expiresIn = ((decoded.exp - nowSecs) / 60).toFixed(2);
  console.info(
    `credentialsSet: token expires in %c${expiresIn}%cminutes`,
    'font-weight: bold; color: yellow;',
  );

  localStorage.setItem('token', token);
  localStorage.removeItem('minToken');
  localStorage.removeItem('awaitingTwoFactor');

  // convert store list (Array of Store ID's) to INTEGERS
  decoded.store_list = decoded.store_list.map((v) => +v);

  state.isAuthenticated = true;
  state.hasTokenAwaitingTwoFactor = false;
  state.token = token;
  state.username = decoded.username;
  state.userId = decoded.user_id;
  state.adminType = decoded.admin_type;
  state.siteList = decoded.store_list;

  if (!update) state.filteredSiteList = [];
  state.userSitesPermissions = decoded.store_permissions;
}

const initialState: AuthState = getInitialState();

// should introduce a async Thunk to handle Logging in
export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (_state) => {
      localStorage.removeItem('token');
      localStorage.removeItem('minToken');
      localStorage.removeItem('awaitingTwoFactor');
      console.info('removed Token');
      return {
        isAuthenticated: false,
        hasTokenAwaitingTwoFactor: false,
        token: '',
        username: '',
        userId: 0,
        adminType: 'user',
        siteList: [],
        filteredSiteList: [],
        setFilteredSitesList: false,
        userPermissions: {
          terminalMode: false,
          showLEO: false,
          editFloorplan: false,
        },
        sitePermissionsList: _state.sitePermissionsList,
        userSitesPermissions: {},
      };
    }, // we will have to create a new action here
    setMinimalToken: (
      state,
      {
        payload: { token: minToken, awaitingTwoFactor },
      }: PayloadAction<{ token: string; awaitingTwoFactor?: boolean }>,
    ) => {
      const decoded = jwtDecode(minToken) as MinimalDecodedToken;

      state.token = minToken;
      state.username = decoded.username;
      if (awaitingTwoFactor) {
        state.hasTokenAwaitingTwoFactor = true;
        localStorage.setItem('minToken', minToken);
        // I may want to save a timestamp so I can invalidate this in 10 minutes
        localStorage.setItem('awaitingTwoFactor', true.toString());
      }
      // maybe save this in localStorage to facilitate 2Factor autoFill from email
    },
    setCredentials: (
      state,
      { payload: { token } }: PayloadAction<{ token: string }>,
    ) => {
      handleCredentials(state, token);
    },
    updateFilteredSiteList: (state, action: PayloadAction<number[]>) => {
      state.setFilteredSitesList = true;
      state.filteredSiteList = action.payload;
    },
    addNewSiteIdToSiteList: (state, action: PayloadAction<number>) => {
      state.siteList.push(action.payload);
      const sitesAddedStr = localStorage.getItem(AddedSitesKey);
      if (sitesAddedStr) {
        try {
          const sitesAdded = JSON.parse(sitesAddedStr) as number[];
          sitesAdded.push(action.payload);
          localStorage.setItem(AddedSitesKey, JSON.stringify(sitesAdded));
        } catch (err) {
          console.warn(
            'Failed to update addedSites in localStorage probably because invalid JSON',
          );
          console.error(err);
        }
      } else {
        localStorage.setItem(AddedSitesKey, JSON.stringify([action.payload]));
      }
    },
  },
  extraReducers(builder) {
    builder.addMatcher(
      authApi.endpoints.twoFactor.matchFulfilled,
      (state, { payload: { token } }) => {
        handleCredentials(state, token);
      },
    );
    builder.addMatcher(
      authApi.endpoints.login.matchFulfilled,
      (state, { payload: { token } }) => {
        const decodedToken = jwtDecode(token) as
          | DecodedToken
          | MinimalDecodedToken;
        // if the token is not minimal after login we want to set the credentials because they are authorized
        if (!decodedToken.minimal) {
          handleCredentials(state, token);
        }
      },
    );
    builder.addMatcher(
      authApi.endpoints.refreshToken.matchFulfilled,
      (state, { payload: { token } }) => {
        handleCredentials(state, token);
      },
    );
    builder.addMatcher(
      authApi.endpoints.updateToken.matchFulfilled,
      (state, { payload: { token } }) => {
        handleCredentials(state, token, true);
      },
    );
    builder.addMatcher(
      sitesApi.endpoints.getSitesPermissions.matchFulfilled,
      (state, { payload }) => {
        console.info('%cPermission List set', 'color: green');
        state.sitePermissionsList = payload; // I want to sort ther permissions so they are displayed in a certain way (required_permission comes before the permission that requires it)
      },
    );
  },
});

export const {
  logout,
  setMinimalToken,
  setCredentials,
  updateFilteredSiteList,
  addNewSiteIdToSiteList,
} = authSlice.actions;

export const selectAuthState = (state: RootState) => state.auth;
export const selectAuthToken = (state: RootState) => state.auth.token;
export const selectAdminType = (state: RootState) => state.auth.adminType;

export const selectUsername = (state: RootState) => state.auth.username;
export const selectUserId = (state: RootState) => state.auth.userId;
export const selectSiteList = (state: RootState) => state.auth.siteList;

export const selectHasSetFilteredSiteList = (state: RootState) =>
  state.auth.setFilteredSitesList;
export const selectFilteredSiteList = (state: RootState) =>
  state.auth.filteredSiteList;

export const selectAuthChainInfo = (state: RootState) => ({
  adminType: state.auth.adminType,
});

export const selectIsAuthenticated = (state: RootState) =>
  state.auth.isAuthenticated;

// USER PERMISSIONS
export const selectIsRegularUser = (state: RootState) =>
  state.auth.adminType === 'user';
export const selectIsChainAdmin = (state: RootState) =>
  state.auth.adminType === 'admin';
export const selectIsGlobalAdmin = (state: RootState) =>
  state.auth.adminType === 'global';

export const selectCanViewAdminPortal = (state: RootState) => {
  return state.auth.adminType === 'global' || state.auth.adminType === 'admin';
};
// All users can view Perforamance Report (LegacyName='analytics')
// export const selectCanViewAnalytics = (state: RootState) => {
//   return state.auth.adminType === "global" || state.auth.adminType === "admin";
// };
// export const selectCanDeleteController = (state: RootState) => {
//   return state.auth.adminType === "global" || state.auth.adminType === "admin";
// };

// TODO
// I should change the wording of this to just "PERMISSIONS"
export const selectSitePermissionsList = (state: RootState) => {
  return state.auth.sitePermissionsList;
};

export const selectAdminUserPermission = (state: RootState) => {
  return state.auth.sitePermissionsList.find(
    (p) => p.permission_id === Permissions.AdminUser,
  );
};
export const selectSiteManagerUserPermission = (state: RootState) => {
  return state.auth.sitePermissionsList.find(
    (p) => p.permission_id === Permissions.SiteManagerUser,
  );
};

//TODO
// Chnage wording to UserPermissions
export const selectUserSitesPermissions = (state: RootState) => {
  return state.auth.userSitesPermissions;
};

// used internally for the Derived Selectors
export const selectSiteId = (_state: RootState, siteId: number) => siteId;
export const selectSiteListArg = (
  _state: RootState,
  siteList: SiteType[] | undefined,
) => siteList;
const selectSiteFromCache = (state: RootState, siteId: number) => {
  const keys = Object.keys(state.api.queries);
  const getSitesKey = keys.find((k) => k.includes('getSites('));
  if (getSitesKey) {
    const sites = state.api.queries[getSitesKey]?.data as
      | SiteType[]
      | undefined;
    return sites?.find((s) => s.store_num === siteId);
  }
};

export const selectSiteOperators = (state: RootState) => {
  // TODO make sure I am selecting the correct key
  const keys = Object.keys(state.api.queries);
  const getSitesKey = keys.find((k) => k.includes('getSites('));
  const operators: string[] = [];
  if (getSitesKey) {
    const sites = state.api.queries[getSitesKey]?.data as
      | SiteType[]
      | undefined;
    if (sites) {
      for (const site of sites) {
        let group = site.store_operator;
        if (group === '') group = 'No Group';
        if (!operators.includes(group)) {
          operators.push(group);
        }
      }
    }
  }
  return operators.sort();
};

// Derived Selectors
export const selectCanReceiveLeoStatusEmailsForSite = createSelector(
  [selectSitePermissionsList, selectUserSitesPermissions, selectSiteId],
  (permissionsList, sitesPermissions, siteId) => {
    const permission = permissionsList.find(
      (p) => p.permission_id === Permissions.ReceiveLeoStatusEmail,
    );
    if (!permission) return false;

    const sitePermissions = sitesPermissions?.[siteId] ?? [];
    if (sitePermissions?.length === 0) return false;

    return sitePermissions.includes(permission.permission_id);
  },
);
export const selectOptInLeoStatusEmailsForSite = createSelector(
  [selectSitePermissionsList, selectUserSitesPermissions, selectSiteId],
  (permissionsList, sitesPermissions, siteId) => {
    const permission = permissionsList.find(
      (p) => p.permission_id === Permissions.OptLeoStatusEmail,
    );
    if (!permission) return false;

    const sitePermissions = sitesPermissions?.[siteId] ?? [];
    if (sitePermissions?.length === 0) return false;

    return sitePermissions.includes(permission.permission_id);
  },
);
export const selectCanReceiveAnalyticsReportEmailsForSite = createSelector(
  [selectSitePermissionsList, selectUserSitesPermissions, selectSiteId],
  (permissionsList, sitesPermissions, siteId) => {
    const permission = permissionsList.find(
      (p) => p.permission_id === Permissions.ReceiveAnalyticReportEmails,
    );
    if (!permission) return false;

    const sitePermissions = sitesPermissions?.[siteId] ?? [];
    if (sitePermissions?.length === 0) return false;

    return sitePermissions.includes(permission.permission_id);
  },
);
export const selectOptInRackUsageEmailsForSite = createSelector(
  [selectSitePermissionsList, selectUserSitesPermissions, selectSiteId],
  (permissionsList, sitesPermissions, siteId) => {
    const permission = permissionsList.find(
      (p) => p.permission_id === Permissions.OptRackUsageEmail,
    );
    if (!permission) return false;

    const sitePermissions = sitesPermissions?.[siteId] ?? [];
    if (sitePermissions?.length === 0) return false;

    return sitePermissions.includes(permission.permission_id);
  },
);
export const selectOptInLiquidLevelEmailsForSite = createSelector(
  [selectSitePermissionsList, selectUserSitesPermissions, selectSiteId],
  (permissionsList, sitesPermissions, siteId) => {
    const permission = permissionsList.find(
      (p) => p.permission_id === Permissions.OptLiquidLevelLeakEmail,
    );
    if (!permission) return false;

    const sitePermissions = sitesPermissions?.[siteId] ?? [];
    if (sitePermissions?.length === 0) return false;

    return sitePermissions.includes(permission.permission_id);
  },
);
export const selectOptInCaseTempEmailsForSite = createSelector(
  [selectSitePermissionsList, selectUserSitesPermissions, selectSiteId],
  (permissionsList, sitesPermissions, siteId) => {
    const permission = permissionsList.find(
      (p) => p.permission_id === Permissions.OptCaseTempEmail,
    );
    if (!permission) return false;

    const sitePermissions = sitesPermissions?.[siteId] ?? [];
    if (sitePermissions?.length === 0) return false;

    return sitePermissions.includes(permission.permission_id);
  },
);
export const selectOptInAlarmReportEmailsForSite = createSelector(
  [selectSitePermissionsList, selectUserSitesPermissions, selectSiteId],
  (permissionsList, sitesPermissions, siteId) => {
    const permission = permissionsList.find(
      (p) => p.permission_id === Permissions.OptAlarmReportEmail,
    );
    if (!permission) return false;

    const sitePermissions = sitesPermissions?.[siteId] ?? [];
    if (sitePermissions?.length === 0) return false;

    return sitePermissions.includes(permission.permission_id);
  },
);

export const selectCanRemoteAccessLeoForSite = createSelector(
  [
    selectIsGlobalAdmin,
    selectSitePermissionsList,
    selectUserSitesPermissions,
    selectSiteId,
  ],
  (isGlobalAdmin, permissionsList, sitesPermissions, siteId) => {
    if (isGlobalAdmin) return true;

    const permission = permissionsList.find(
      (p) => p.permission_id === Permissions.RemoteAccessLeo,
    );
    if (!permission) return false;

    const sitePermissions = sitesPermissions?.[siteId] ?? [];
    if (sitePermissions?.length === 0) return false;

    return sitePermissions.includes(permission.permission_id);
  },
);

export const selectCanRemoteAccessLeo = createSelector(
  [selectIsGlobalAdmin, selectSiteList, (state: RootState) => state],
  (isGlobalAdmin, siteList, state) => {
    if (isGlobalAdmin) return true;
    return siteList.some((siteId) =>
      selectCanRemoteAccessLeoForSite(state, siteId),
    );
  },
);

export const selectCanRemoteAccessControllerForSite = createSelector(
  [
    selectIsGlobalAdmin,
    selectSitePermissionsList,
    selectUserSitesPermissions,
    selectSiteId,
  ],
  (isGlobalAdmin, permissionsList, sitesPermissions, siteId) => {
    if (isGlobalAdmin) return true;

    const permission = permissionsList.find(
      (p) => p.permission_id === Permissions.RemoteAccessController,
    );
    if (!permission) return false;

    const sitePermissions = sitesPermissions?.[siteId] ?? [];
    if (sitePermissions?.length === 0) return false;

    return sitePermissions.includes(permission.permission_id);
  },
);

export const selectCanRemoteAccessController = createSelector(
  [selectIsGlobalAdmin, selectSiteList, (state: RootState) => state],
  (isGlobalAdmin, siteList, state) => {
    if (isGlobalAdmin) return true;
    return siteList.some((siteId) =>
      selectCanRemoteAccessControllerForSite(state, siteId),
    );
  },
);

export const selectCanViewRemoteAccessPorts = createSelector(
  [
    selectIsGlobalAdmin,
    selectSitePermissionsList,
    selectUserSitesPermissions,
    selectSiteId,
  ],
  (isGlobalAdmin, permissionsList, sitesPermissions, siteId) => {
    if (isGlobalAdmin) return true;

    const permission = permissionsList.find(
      (p) => p.permission_id === Permissions.RemoteAccessPorts,
    );
    if (!permission) return false;

    const sitePermissions = sitesPermissions?.[siteId] ?? [];
    if (sitePermissions?.length === 0) return false;

    return sitePermissions.includes(permission.permission_id);
  },
);

export const selectCanViewFloorplanForSite = createSelector(
  [
    selectIsGlobalAdmin,
    selectSitePermissionsList,
    selectUserSitesPermissions,
    selectSiteId,
  ],
  (isGlobalAdmin, permissionsList, sitesPermissions, siteId) => {
    if (isGlobalAdmin) return true;

    const permission = permissionsList.find(
      (p) => p.permission_id === Permissions.ViewFloorplan,
    );
    if (!permission) return false;

    const sitePermissions = sitesPermissions?.[siteId] ?? [];
    if (sitePermissions?.length === 0) return false;

    return sitePermissions.includes(permission.permission_id);
  },
);
export const selectCanEditFloorplanForSite = createSelector(
  [
    selectIsGlobalAdmin,
    selectSitePermissionsList,
    selectUserSitesPermissions,
    selectSiteId,
  ],
  (isGlobalAdmin, permissionsList, sitesPermissions, siteId) => {
    if (isGlobalAdmin) return true;

    const permission = permissionsList.find(
      (p) => p.permission_id === Permissions.EditFloorplan,
    );
    if (!permission) return false;

    const sitePermissions = sitesPermissions?.[siteId] ?? [];
    if (sitePermissions?.length === 0) return false;

    return sitePermissions.includes(permission.permission_id);
  },
);
export const selectCanEditControllerBackupsForSite = createSelector(
  [
    selectIsGlobalAdmin,
    selectSitePermissionsList,
    selectUserSitesPermissions,
    selectSiteId,
  ],
  (isGlobalAdmin, permissionsList, sitesPermissions, siteId) => {
    if (isGlobalAdmin) return true;

    const permission = permissionsList.find(
      (p) => p.permission_id === Permissions.EditControllerBackups,
    );
    if (!permission) return false;

    const sitePermissions = sitesPermissions?.[siteId] ?? [];
    if (sitePermissions?.length === 0) return false;

    return sitePermissions.includes(permission.permission_id);
  },
);

export const selectIsAdminForSite = createSelector(
  [
    selectSitePermissionsList,
    selectUserSitesPermissions,
    selectIsGlobalAdmin,
    selectSiteId,
  ],
  (permissionsList, sitesPermissions, isGlobalAdmin, siteId) => {
    // admin_users has some limitations and we don't want to apply those limitations if the user is global
    if (isGlobalAdmin) return false;

    const permission = permissionsList.find(
      (p) => p.permission_id === Permissions.AdminUser,
    );
    if (!permission) return false;

    const sitePermissions = sitesPermissions?.[siteId] ?? [];
    if (sitePermissions?.length === 0) return false;

    return sitePermissions.includes(permission.permission_id);
  },
);
export const selectIsSiteManagerForSite = createSelector(
  [
    selectSitePermissionsList,
    selectUserSitesPermissions,
    selectIsGlobalAdmin,
    selectIsAdminForSite,
    selectSiteId,
  ],
  (
    permissionsList,
    sitesPermissions,
    isGlobalAdmin,
    isAdminForSite,
    siteId,
  ) => {
    if (isGlobalAdmin || isAdminForSite) return false;

    const permission = permissionsList.find(
      (p) => p.permission_id === Permissions.SiteManagerUser,
    );
    if (!permission) return false;

    const sitePermissions = sitesPermissions?.[siteId] ?? [];
    if (sitePermissions?.length === 0) return false;

    return sitePermissions.includes(permission.permission_id);
  },
);

export const selectCanEditController = createSelector(
  [selectIsGlobalAdmin, selectIsChainAdmin, selectIsSiteManagerForSite],
  (isGlobalAdmin, isChainAdmin, isSiteManagerForSite) => {
    return isGlobalAdmin || isChainAdmin || isSiteManagerForSite;
  },
);

export const selectCanEditUserPermissionsForSite = createSelector(
  [
    selectIsGlobalAdmin,
    selectIsAdminForSite,
    selectSitePermissionsList,
    selectUserSitesPermissions,
    selectSiteId,
  ],
  (
    isGlobalAdmin,
    isAdminForSite,
    permissionsList,
    sitesPermissions,
    siteId,
  ) => {
    if (isGlobalAdmin) return true;
    if (isAdminForSite) return true;

    const permission = permissionsList.find(
      (p) => p.permission_id === Permissions.EditOtherUsers,
    );
    if (!permission) return false;

    const sitePermissions = sitesPermissions?.[siteId] ?? [];
    if (sitePermissions?.length === 0) return false;

    return sitePermissions.includes(permission.permission_id);
  },
);

export const selectCanAddUserForSite = createSelector(
  [
    selectIsGlobalAdmin,
    selectCanEditUserPermissionsForSite,
    selectSitePermissionsList,
    selectUserSitesPermissions,
    selectSiteId,
  ],
  (
    isGlobalAdmin,
    canEditUserPermissions,
    permissionsList,
    sitesPermissions,
    siteId,
  ) => {
    if (isGlobalAdmin) return true;
    if (canEditUserPermissions) return true;

    const permission = permissionsList.find(
      (p) => p.permission_id === Permissions.AddUser,
    );
    if (!permission) return false;

    const sitePermissions = sitesPermissions?.[siteId] ?? [];
    if (sitePermissions?.length === 0) return false;

    return sitePermissions.includes(permission.permission_id);
  },
);

export const selectCanEditSiteInfo = createSelector(
  [selectIsAdminForSite, selectIsGlobalAdmin],
  (isAdminForSite, isGlobalAdmin) => {
    return isAdminForSite || isGlobalAdmin;
  },
);

export const selectCanChangePhoneNumbersForSite = createSelector(
  [
    selectIsGlobalAdmin,
    selectSitePermissionsList,
    selectUserSitesPermissions,
    selectSiteId,
  ],
  (isGlobalAdmin, permissionsList, sitesPermissions, siteId) => {
    if (isGlobalAdmin) return true;

    const permission = permissionsList.find(
      (p) => p.permission_id === Permissions.EditPhoneNumbers,
    );
    if (!permission) return false;

    const sitePermissions = sitesPermissions?.[siteId] ?? [];
    if (sitePermissions?.length === 0) return false;

    return sitePermissions.includes(permission.permission_id);
  },
);

export const selectCanEditLeoAlertScheduleForSite = createSelector(
  [
    selectIsGlobalAdmin,
    selectSitePermissionsList,
    selectUserSitesPermissions,
    selectSiteId,
  ],
  (isGlobalAdmin, permissionsList, sitesPermissions, siteId) => {
    if (isGlobalAdmin) return true;

    const permission = permissionsList.find(
      (p) => p.permission_id === Permissions.EditLeoAlertSchedule,
    );
    if (!permission) return false;

    const sitePermissions = sitesPermissions?.[siteId] ?? [];
    if (sitePermissions?.length === 0) return false;

    return sitePermissions.includes(permission.permission_id);
  },
);

export const selectUserCanEditSomeSitesLeoAlertReceivers = createSelector(
  [selectIsGlobalAdmin, selectSitePermissionsList, selectUserSitesPermissions],
  (isGlobalAdmin, permissionsList, sitesPermissions) => {
    if (isGlobalAdmin) return true;

    const permission = permissionsList.find(
      (p) => p.permission_id === Permissions.EditPhoneNumbers,
    );
    if (!permission) return false;

    const hasPermissionInSomeSites = Object.values(sitesPermissions).some(
      (sitePermissions: number[]) => {
        if (sitePermissions?.length === 0) return false;
        return sitePermissions.includes(permission.permission_id);
      },
    );

    return hasPermissionInSomeSites;
  },
);

export const selectSitesWithLeoAlertAndUserCanEdit = createSelector(
  [
    selectIsGlobalAdmin,
    selectSitePermissionsList,
    selectUserSitesPermissions,
    selectSiteListArg,
  ],
  (isGlobalAdmin, permissionsList, sitesPermissions, siteList) => {
    if (isGlobalAdmin) return siteList;

    return siteList?.filter((site) => {
      const permission = permissionsList.find(
        (p) => p.permission_id === Permissions.EditPhoneNumbers,
      );
      if (!permission) return false;

      const sitePermissions = sitesPermissions?.[site.store_num] ?? [];
      if (sitePermissions?.length === 0) return false;

      return sitePermissions.includes(permission.permission_id);
    });
  },
);

export const selectCanViewSite = createSelector(
  [
    selectIsGlobalAdmin,
    selectSitePermissionsList,
    selectUserSitesPermissions,
    selectSiteId,
  ],
  (isGlobalAdmin, permissionsList, sitesPermissions, siteId) => {
    if (isGlobalAdmin) return true;

    const permission = permissionsList.find(
      (p) => p.permission_id === Permissions.ViewStore,
    );
    if (!permission) return false;

    const sitePermissions = sitesPermissions?.[siteId] ?? [];
    if (sitePermissions?.length === 0) return false;

    return sitePermissions.includes(permission.permission_id);
  },
);

export const selectUserEditablePermissions = createSelector(
  [
    selectSitePermissionsList,
    selectCanReceiveAnalyticsReportEmailsForSite,
    selectCanReceiveLeoStatusEmailsForSite,
  ],
  (permissions, canReceiveAnalyticsEmails, canReceiveLeoEmails) => {
    const perms: GetSitePermissionsResponse = [];
    if (canReceiveLeoEmails) {
      const opt_leo_status_email_permission = permissions.find(
        (p) => p.permission_id === Permissions.OptLeoStatusEmail,
      );
      if (opt_leo_status_email_permission)
        perms.push(opt_leo_status_email_permission);
    }

    if (canReceiveAnalyticsEmails) {
      const optRackUsageEmails = permissions.find(
        (p) => p.permission_id === Permissions.OptRackUsageEmail,
      );
      const optLiquidLevelEmails = permissions.find(
        (p) => p.permission_id === Permissions.OptLiquidLevelLeakEmail,
      );
      const optCaseTempsEmails = permissions.find(
        (p) => p.permission_id === Permissions.OptCaseTempEmail,
      );

      if (optRackUsageEmails) perms.push(optRackUsageEmails);
      if (optLiquidLevelEmails) perms.push(optLiquidLevelEmails);
      if (optCaseTempsEmails) perms.push(optCaseTempsEmails);
    }
    return perms;
  },
);

export const selectDefaultPermissionsForUserTypeAdmin = createSelector(
  [selectSitePermissionsList],
  (permissions) => {
    const defaultPermissions = [];

    const view_store_permission = permissions.find(
      (p) => p.permission_id === Permissions.ViewStore,
    );
    const admin_user_permission = permissions.find(
      (p) => p.permission_id === Permissions.AdminUser,
    );
    const edit_other_user_permission = permissions.find(
      (p) => p.permission_id === Permissions.EditOtherUsers,
    );
    const receive_leo_status_email_permission = permissions.find(
      (p) => p.permission_id === Permissions.ReceiveLeoStatusEmail,
    );
    const opt_leo_status_email_permission = permissions.find(
      (p) => p.permission_id === Permissions.OptLeoStatusEmail,
    );

    if (view_store_permission) defaultPermissions.push(view_store_permission);
    if (admin_user_permission) defaultPermissions.push(admin_user_permission);
    if (edit_other_user_permission)
      defaultPermissions.push(edit_other_user_permission);
    if (receive_leo_status_email_permission)
      defaultPermissions.push(receive_leo_status_email_permission);
    if (opt_leo_status_email_permission)
      defaultPermissions.push(opt_leo_status_email_permission);

    return defaultPermissions;
  },
);

function removePermissionsRelatedToSiteFeatures(
  permissionList: GetSitePermissionsResponse,
  site: SiteType | undefined,
) {
  const floorplanAvailable = site?.floor_plan_enabled ?? false;
  const terminalModeAvailable = site?.terminal_mode_enabled ?? false;
  const leoAlertAvailable = site?.voice_sms_subscription !== null;
  const controllerBackupsEnabled = site?.controller_backup_enabled ?? false;
  const refrigerantTrackingEnabled =
    site?.refrigerant_tracking_enabled ?? false;

  const permissions = permissionList.filter((p) => {
    if (p.permission_id === Permissions.EditFloorplan && !floorplanAvailable) {
      return false;
    }

    if (p.permission_id === Permissions.ViewFloorplan && !floorplanAvailable) {
      return false;
    }

    if (
      p.permission_id === Permissions.RemoteAccessLeo &&
      !terminalModeAvailable
    ) {
      return false;
    }

    if (
      p.permission_id === Permissions.RemoteAccessController &&
      !terminalModeAvailable
    ) {
      return false;
    }

    if (
      p.permission_id === Permissions.EditPhoneNumbers &&
      !leoAlertAvailable
    ) {
      return false;
    }

    if (
      p.permission_id === Permissions.EditControllerBackups &&
      !controllerBackupsEnabled
    ) {
      return false;
    }

    if (
      p.permission_id === Permissions.RefrigerantTracking &&
      !refrigerantTrackingEnabled
    ) {
      return false;
    }

    return true;
  });

  return permissions;
}

export const selectIsInglesSite = createSelector(
  [selectSiteId, selectSiteFromCache],
  (_siteId, site) => {
    if (site) {
      return site.store_operator === 'INGLES';
    }
    return false;
  },
);

export const selectIsLeoAlertAvailableForSite = createSelector(
  [selectSiteId, selectSiteFromCache],
  (_siteId, site) => {
    if (site) {
      return site.voice_sms_subscription !== null;
    }

    return false;
  },
);

export const selectIsLeoAlertSMSAvailableForSite = createSelector(
  [selectSiteId, selectSiteFromCache],
  (_siteId, site) => {
    if (site) {
      return site.voice_sms_subscription?.includes('SMS') ?? false;
    }
    return false;
  },
);
export const selectIsLeoAlertVOICEAvailableForSite = createSelector(
  [selectSiteId, selectSiteFromCache],
  (_siteId, site) => {
    if (site) {
      return site.voice_sms_subscription?.includes('VOICE') ?? false;
    }
    return false;
  },
);
export const selectIsLeoAlertEMAILAvailableForSite = createSelector(
  [selectSiteId, selectSiteFromCache],
  (_siteId, site) => {
    if (site) {
      return site.voice_sms_subscription?.includes('EMAIL') ?? false;
    }
    return false;
  },
);

export const selectSyncAlarmsAvailableForSite = createSelector(
  [selectSiteId, selectSiteFromCache],
  (_siteId, site) => {
    if (site) {
      const minVersion = '4.4.1';
      const version = site?.leo_version;
      if (version) {
        const valid = compare(minVersion, version, '<=');
        return valid;
      }
    }

    return false;
  },
);

// TODO
/** TODO
 * REDO
 * NEEDS IMPROVEMENT
 * @description This is a important selector for our Site-Users Logic
 * So we don't reveal any hidden permissions that may be available for other sites/chains
 *
 */
export const selectFilteredPermissionsListForSiteBasedOnUsers = createSelector(
  [
    selectSitePermissionsList,
    selectUserSitesPermissions,
    selectIsGlobalAdmin,
    selectIsAdminForSite,
    selectIsSiteManagerForSite,
    selectCanReceiveLeoStatusEmailsForSite,
    selectCanReceiveAnalyticsReportEmailsForSite,
    selectSiteId,
    selectSiteFromCache,
  ],
  (
    permissionsList,
    userSitesPermissions,
    isGlobalAdmin,
    isAdminForSite,
    isSiteManager,
    canReceiveLeoStatusEmails,
    canReceiveAnalyticsReportEmails,
    siteId,
    site,
  ) => {
    // It was described to me that a user could give any of their permissions to other users
    // But this is actually not the case...
    // Admin User can't give admin_user permission but can give store_manager which it is missing
    // Store Manager Users can't create other Store_manager Users even though they have the permission

    // Global users should be able to give every permission (Besides admin_user <- handled by Admin Creation flow)
    if (isGlobalAdmin) {
      const admin_user_permission = permissionsList.find(
        (p) => p.permission_id === Permissions.AdminUser,
      );
      const adminUserIndex = permissionsList.findIndex(
        (p) => p.permission_id === admin_user_permission?.permission_id,
      );
      const permissionsListCopy = structuredClone(permissionsList);
      if (adminUserIndex > -1) permissionsListCopy.splice(adminUserIndex, 1); // REMOVE admin_user (This will be handled by the API)
      //return permissionsListCopy;
      return removePermissionsRelatedToSiteFeatures(permissionsListCopy, site);
    }
    /*
            1. Admin_User needs store_manager permission available
            2.
         */

    const userPermissions = structuredClone(userSitesPermissions[siteId]) as
      | number[]
      | undefined;

    if (!userPermissions) {
      return [];
    }

    if (isAdminForSite) {
      // add store_manager related permissions
      // remove admin_user permission

      // ADD
      const site_manager_permission = permissionsList.find(
        (p) => p.permission_id === Permissions.SiteManagerUser,
      );
      // ADD
      const edit_user_permission = permissionsList.find(
        (p) => p.permission_id === Permissions.EditOtherUsers,
      );
      // ADD
      const add_user_permission = permissionsList.find(
        (p) => p.permission_id === Permissions.AddUser,
      );
      // ADD (CHECK IF USER HAS THIS FLOOR PLAN AVAILABLE)
      const edit_floorplan_permission = permissionsList.find(
        (p) => p.permission_id === Permissions.EditFloorplan,
      );

      // REMOVE
      const admin_user_permission = permissionsList.find(
        (p) => p.permission_id === Permissions.AdminUser,
      );
      const adminUserIndex = userPermissions.findIndex(
        (p) => p === admin_user_permission?.permission_id,
      );
      if (adminUserIndex > -1) {
        userPermissions.splice(adminUserIndex, 1);
      }

      if (
        site_manager_permission &&
        !userPermissions.includes(site_manager_permission.permission_id)
      )
        userPermissions.push(site_manager_permission.permission_id);

      if (
        edit_user_permission &&
        !userPermissions.includes(edit_user_permission.permission_id)
      )
        userPermissions.push(edit_user_permission.permission_id);

      if (
        add_user_permission &&
        !userPermissions.includes(add_user_permission.permission_id)
      )
        userPermissions.push(add_user_permission.permission_id);

      if (
        edit_floorplan_permission &&
        !userPermissions.includes(edit_floorplan_permission.permission_id)
      )
        userPermissions.push(edit_floorplan_permission.permission_id);
    }

    if (isSiteManager) {
      // remove Site Manager Permissions ("site_manager_user", "edit_users_permission")
      const site_manager_permission = permissionsList.find(
        (p) => p.permission_id === Permissions.SiteManagerUser,
      );

      // ADD
      const edit_user_permission = permissionsList.find(
        (p) => p.permission_id === Permissions.EditOtherUsers,
      );

      // ADD
      const add_user_permission = permissionsList.find(
        (p) => p.permission_id === Permissions.AddUser,
      );

      // ADD ???
      // const edit_floorplan_permission = permissionsList.find(
      //   (p) => p.permission_name === Permissions.EditFloorplan,
      // );

      const siteManagerIndex = userPermissions.findIndex(
        (p) => p === site_manager_permission?.permission_id,
      );
      if (siteManagerIndex > -1) {
        userPermissions.splice(siteManagerIndex, 1);
      }

      if (
        edit_user_permission &&
        !userPermissions.includes(edit_user_permission.permission_id)
      )
        userPermissions.push(edit_user_permission.permission_id);

      if (
        add_user_permission &&
        !userPermissions.includes(add_user_permission.permission_id)
      )
        userPermissions.push(add_user_permission.permission_id);
    }

    if (canReceiveLeoStatusEmails) {
      // add opt_leo_status_emails
      const optLeoStatusEmails = permissionsList.find(
        (p) => p.permission_id === Permissions.OptLeoStatusEmail,
      );

      if (
        optLeoStatusEmails &&
        !userPermissions.includes(optLeoStatusEmails.permission_id)
      )
        userPermissions.push(optLeoStatusEmails.permission_id);
    }

    if (canReceiveAnalyticsReportEmails) {
      // add opt_leo_status_emails
      const optRackUsageEmails = permissionsList.find(
        (p) => p.permission_id === Permissions.OptRackUsageEmail,
      );
      const optLiquidLevelEmails = permissionsList.find(
        (p) => p.permission_id === Permissions.OptLiquidLevelLeakEmail,
      );
      const optCaseTempsEmails = permissionsList.find(
        (p) => p.permission_id === Permissions.OptCaseTempEmail,
      );

      if (
        optRackUsageEmails &&
        !userPermissions.includes(optRackUsageEmails.permission_id)
      ) {
        userPermissions.push(optRackUsageEmails.permission_id);
      }

      if (
        optLiquidLevelEmails &&
        !userPermissions.includes(optLiquidLevelEmails.permission_id)
      ) {
        userPermissions.push(optLiquidLevelEmails.permission_id);
      }

      if (
        optCaseTempsEmails &&
        !userPermissions.includes(optCaseTempsEmails.permission_id)
      ) {
        userPermissions.push(optCaseTempsEmails.permission_id);
      }
    }

    const filteredList = permissionsList.filter((perm) => {
      return userPermissions.includes(perm.permission_id);
    });

    // return filteredList;
    return removePermissionsRelatedToSiteFeatures(filteredList, site);
  },
);

export default authSlice.reducer;

function filterSite(
  site: SiteType,
  state: RootState,
  siteFilterBy: SiteFilterBy,
  siteFilterValue: string,
) {
  const isFilteredOperator = !state.sites.filteredSiteOperators.includes(
    site.store_operator ?? 'No Group',
  );

  if (siteFilterBy === 'All') {
    const quickSearchText = [
      site.store_name,
      site.store_state,
      site.store_city,
      site.store_order,
      site.store_operator,
    ]
      .join(' ')
      .toLowerCase();
    const isFilteredValue = quickSearchText.includes(
      siteFilterValue.toLowerCase(),
    );

    return isFilteredOperator && isFilteredValue;
  }
  if (siteFilterBy === 'controller_type') {
    const isFilteredValue =
      site.controllers?.some((controller) => {
        return (
          `${controller.controller_type}`.toLowerCase() ===
          siteFilterValue.toLowerCase()
        );
      }) ?? false;

    return isFilteredOperator && isFilteredValue;
  }
  if (siteFilterBy === 'subscriptionStatus') {
    if (!site.cloud_end_date)
      return isFilteredOperator && siteFilterValue === 'n/a';

    if (siteFilterValue === 'n/a') return isFilteredOperator && false;

    const today = DateTime.now()
      .setZone('utc')
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    const expirationDate = DateTime.fromISO(site.cloud_end_date).setZone('utc');
    const isFilteredValue =
      siteFilterValue === 'expired'
        ? today >= expirationDate
        : !(today >= expirationDate);

    return isFilteredOperator && isFilteredValue;
  }

  const isFilteredValue = `${site[siteFilterBy]}`
    .toLowerCase()
    .includes(siteFilterValue.toLowerCase());

  return isFilteredOperator && isFilteredValue;
}

let isFirstCall = true;
startAppListening({
  predicate: (_action, currState) => {
    // determine if we should run the effect below
    const { data } = sitesApi.endpoints.getSites.select({
      store_list: currState.auth.siteList,
    })(currState);

    const siteFilterValue = currState.sites.filterValue;
    const siteFilterBy = currState.sites.filterBy;
    const filteredSiteOperatorsLength =
      currState.sites.filteredSiteOperators?.length;

    if ((filteredSiteOperatorsLength > 0 || siteFilterValue !== '') && data) {
      const myFilteredSiteList = structuredClone(data)
        .filter((site) =>
          filterSite(site, currState, siteFilterBy, siteFilterValue),
        )
        .map((v) => v.store_num);

      if (
        // This comparison seems wrong
        Array.from(myFilteredSiteList).sort().join(',') !==
        Array.from(currState.auth.filteredSiteList)?.sort()?.join(',')
      ) {
        return true;
      }

      return false;
    } else if (
      (filteredSiteOperatorsLength === 0 || siteFilterValue === '') &&
      data &&
      currState.auth.filteredSiteList?.length < currState.auth.siteList.length
    ) {
      return true;
    }
    return false;
  },
  effect: (_action, listenerApi) => {
    const debouncedEffect = debounce(async () => {
      const state = listenerApi.getState();

      const { data } = sitesApi.endpoints.getSites.select({
        store_list: state.auth.siteList,
      })(state);

      const siteFilterBy = state.sites.filterBy;
      const siteFilterValue = state.sites.filterValue;
      const filteredSiteOperatorsLength =
        state.sites.filteredSiteOperators?.length;

      if (
        (filteredSiteOperatorsLength > 0 || siteFilterValue !== '') &&
        data &&
        state.auth.filteredSiteList
      ) {
        const myFilteredSiteList = structuredClone(data)
          .filter((site) =>
            filterSite(site, state, siteFilterBy, siteFilterValue),
          )
          .map((v) => v.store_num);

        if (
          // This comparison seems wrong
          Array.from(myFilteredSiteList).sort().join(',') !==
          Array.from(state.auth.filteredSiteList)?.sort()?.join(',')
        ) {
          listenerApi.dispatch(updateFilteredSiteList(myFilteredSiteList));
        }
      } else if (
        filteredSiteOperatorsLength === 0 &&
        siteFilterValue === '' &&
        state.auth.filteredSiteList?.length < state.auth.siteList.length
      ) {
        listenerApi.dispatch(updateFilteredSiteList(state.auth.siteList));
      }
    }, 350);

    if (isFirstCall) {
      isFirstCall = false;
      debouncedEffect();
      debouncedEffect.flush();
    } else {
      debouncedEffect();
    }
  },
});
